import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { compressMessage, decompressMessage } from "../utils/Compression";

const WebSocketContext = createContext(null);
const host = window?.location?.hostname ?? "localhost";

export class ChunkReceiver {
    constructor(onComplete, onProgress) {
        this.buffers = {};
        this.onComplete = onComplete;
        this.onProgress = onProgress;
        this.timeouts = {};
    }

    handleMessage(message) {
        if (message.tp !== "ch") return false;

        const { id, i, t, d } = message;

        if (!this.buffers[id]) {
            this.buffers[id] = {
                received: 0,
                t,
                chunks: new Array(t)
            };
            this.timeouts[id] = setTimeout(() => {
                if (this.buffers[id]) {
                    console.warn(`[ChunkReceiver] Timed out for message ${id}: ${this.buffers[id].received}/${t} chunks`);
                    delete this.buffers[id];
                    delete this.timeouts[id];
                }
            }, 15000);
        }

        const entry = this.buffers[id];
        if (entry.chunks[i] !== undefined) return true;

        entry.chunks[i] = d;
        entry.received++;

        const progress = Math.floor((entry.received / t) * 100);
        if (this.onProgress) this.onProgress(progress, entry.received, t);

        if (entry.received === t) {
            clearTimeout(this.timeouts[id]);
            delete this.timeouts[id];
            delete this.buffers[id];
            const fullBase64 = entry.chunks.join("");
            let fullString;
            try {
                const binaryString = atob(fullBase64);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let k = 0; k < len; k++) {
                    bytes[k] = binaryString.charCodeAt(k);
                }
                const text = new TextDecoder('utf-8').decode(bytes);
                const json = JSON.parse(text);
                this.onComplete(json);
            } catch (e) {
                console.error("Ошибка декодирования/парсинга чанков:", e);
            }
        }

        return true;
    }

    cleanup() {
        Object.keys(this.timeouts).forEach(id => {
            clearTimeout(this.timeouts[id]);
        });
        this.buffers = {};
        this.timeouts = {};
    }
}

export const WebSocketProvider = ({ children }) => {
    const [ws, setWs] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const [ws_data, setWsData] = useState(null);

    const latency = useRef(null);
    const wsRef = useRef(null);
    const reconnectTimeout = useRef(null);
    const pingInterval = useRef(null);
    const lastPingSent = useRef(null);
    const missedPongs = useRef(0);
    const chunkReceiverRef = useRef(null); // Используем useRef для chunkReceiver

    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);

    // ИНИЦИАЛИЗАЦИЯ КАК NULL вместо {} - это важно!
    const [refresh, setRefresh] = useState(null);
    const [sets, setSets] = useState(null);
    const [auth, setAuth] = useState(null);
    const [users_list, setUsersList] = useState(null);
    const [all_users_list, setAllUsersList] = useState(null);
    const [scoreboard, setScoreboard] = useState(null);
    const [results, setResults] = useState(null);
    const [profile, setProfile] = useState(null);
    const [config_ui, setConfig_ui] = useState(null);
    const [refresh_config, setRefresh_config] = useState(null);
    const [judging_start, setJudging_start] = useState(null);
    const [judging_done, setJudging_done] = useState(null);
    const [judging_clear, setJudging_clear] = useState(null);
    const [judging_refresh, setJudging_refresh] = useState(null);
    const [display, setDisplay] = useState(null);

    const handlers = {
        auth: (msg) => setAuth(msg || null), // Гарантируем, что не будет undefined
        sets: (msg) => setSets(msg || null),
        users_list: (msg) => setUsersList(msg || null),
        all_users_list: (msg) => setAllUsersList(msg || null),
        refresh: (msg) => setRefresh(msg || null),
        scoreboard: (msg) => setScoreboard(msg || null),
        results: (msg) => setResults(msg || null),
        profile: (msg) => setProfile(msg || null),
        config_ui: (msg) => setConfig_ui(msg || null),
        refresh_config: (msg) => setRefresh_config(msg || null),
        judging_start: (msg) => setJudging_start(msg || null),
        judging_done: (msg) => setJudging_done(msg || null),
        judging_clear: (msg) => setJudging_clear(msg || null),
        judging_refresh: (msg) => setJudging_refresh(msg || null),
        display: (msg) => setDisplay(msg || null),
    };

    function handleMessage(msg) {
        const handler = handlers[msg.type || msg.action];
        if (handler) handler(msg);
        else console.warn("ROUTING Unk WS message:", msg);
    }

    useEffect(() => {
        if (progress > 100) {
            setProgress(0);
            setLoading(false);
        }
    }, [progress]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        // Создаем chunkReceiver ДО определения обработчиков
        chunkReceiverRef.current = new ChunkReceiver(
            (fullData) => {
                console.log("📩 [Основной WS] Сообщение:", fullData);
                setWsData(fullData);
                setLoading(false);
                setProgress(0);
                handleMessage(fullData);
            },
            (percent) => {
                setLoading(true);
                setProgress(percent);
            }
        );

        const closeConnection = () => {
            console.warn("🛑 Закрываем WebSocket...");
            clearInterval(pingInterval.current);
            clearTimeout(reconnectTimeout.current);

            if (chunkReceiverRef.current) {
                chunkReceiverRef.current.cleanup();
            }

            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.close();
                wsRef.current = null;
            }
        };

        const startPing = () => {
            clearInterval(pingInterval.current);

            pingInterval.current = setInterval(async () => {
                if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
                if (missedPongs.current >= 3) {
                    console.warn("❌ Пропущено 3 PONG — закрываем соединение");
                    wsRef.current.close();
                    setIsConnected(false);
                    return;
                }

                lastPingSent.current = Date.now();
                missedPongs.current++;

                wsRef.current.zsend(JSON.stringify({ a: "p" }));
            }, 5000);
        };

        const connectWebSocket = () => {
            closeConnection();

            console.log(`🔄 Подключаемся к wss://${host}/rrj_ws ...`);

            const socket = new WebSocket(`wss://${host}/rrj_ws`, token, {
                perMessageDeflate: true
            });

            wsRef.current = socket;
            setWs(socket);

            socket.onopen = () => {
                console.log("✅ WebSocket подключён!");
                setIsConnected(true);
                setError(null);
                missedPongs.current = 0;
                startPing();
            };

            socket.onmessage = async (ev) => {
                const msg = JSON.parse(await decompressMessage(ev.data));

                if (msg.a === "p") { // pong
                    latency.current = Date.now() - lastPingSent.current;
                    missedPongs.current = 0;
                    return;
                }

                if (msg.tp === "ch") { // chunk/heartbeat
                    latency.current = Date.now() - lastPingSent.current;
                    missedPongs.current = 0;
                }

                // Используем chunkReceiver из useRef
                if (chunkReceiverRef.current?.handleMessage(msg)) return;

                console.log("📩 [Основной WS] Сообщение:", msg);
                setWsData(msg);
                handleMessage(msg);
            };

            socket.onerror = (err) => {
                console.error("❌ WebSocket ошибка", err);
                setError("Ошибка WebSocket");
            };

            socket.onclose = () => {
                console.warn("🔌 WebSocket закрыт. Переподключение…");
                setIsConnected(false);
                reconnectTimeout.current = setTimeout(connectWebSocket, 5000);
            };

            socket.zsend = async (data) => {
                if (socket.readyState === WebSocket.OPEN) {
                    socket.send(await compressMessage(data));
                }
            };
        };

        connectWebSocket();

        return () => closeConnection();
    }, []);

    return (
        <WebSocketContext.Provider value={{
            ws,
            isConnected,
            auth,
            ws_data,
            latency,
            loading,
            progress,
            sets,
            users_list,
            all_users_list,
            scoreboard,
            refresh,
            results,
            profile,
            config_ui,
            refresh_config,
            judging_start,
            judging_done,
            judging_clear,
            judging_refresh,
            display
        }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => useContext(WebSocketContext);